/**
 * @fileoverview Module Context Provider for shared state and services
 * 
 * The ModuleContextProvider creates a React context that provides shared
 * state and services to all modules within the application.
 */

import React, { createContext, useContext, useMemo, useCallback, useEffect, useState } from 'react';
import { moduleRegistry } from '../core/ModuleRegistry.js';
import { moduleEventBus } from '../core/ModuleEventBus.js';
import { ModuleEventType } from '../types/index.js';

/**
 * React context for module system
 */
const ModuleContext = createContext(null);

/**
 * Hook to access module context
 * 
 * @returns {ModuleContext} The module context
 * @throws {Error} If used outside of ModuleContextProvider
 */
export const useModuleContext = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModuleContext must be used within a ModuleContextProvider');
  }
  return context;
};

/**
 * Hook to access module events
 * 
 * @param {string} eventType - Event type to subscribe to (use '*' for all events)
 * @param {function(ModuleEvent): void} callback - Event handler callback
 * @param {Object} [options] - Subscription options
 * @param {string[]} [options.permissions] - Required permissions to receive events
 * @param {string} [options.sourceFilter] - Only receive events from specific source
 * @param {boolean} [options.enabled=true] - Whether the subscription is enabled
 * @returns {Object} Event subscription utilities
 */
export const useModuleEvents = (eventType, callback, options = {}) => {
  const { eventBus } = useModuleContext();
  const { enabled = true, ...subscriptionOptions } = options;

  useEffect(() => {
    if (!enabled || !callback) {
      return;
    }

    const unsubscribe = eventBus.subscribe(eventType, callback, subscriptionOptions);
    return unsubscribe;
  }, [eventBus, eventType, callback, enabled, subscriptionOptions]);

  const emit = useCallback((event) => {
    eventBus.emit(event);
  }, [eventBus]);

  const getHistory = useCallback((filters) => {
    return eventBus.getHistory(filters);
  }, [eventBus]);

  return {
    emit,
    getHistory,
    eventBus
  };
};

/**
 * Hook to access module registry
 * 
 * @returns {Object} Registry utilities
 */
export const useModuleRegistry = () => {
  const { registry, permissions } = useModuleContext();

  const getAccessibleModules = useCallback(() => {
    return registry.getAccessibleModules(permissions);
  }, [registry, permissions]);

  const getModulesByContext = useCallback((context) => {
    return registry.getByContext(context);
  }, [registry]);

  const hasModule = useCallback((moduleId) => {
    return registry.has(moduleId);
  }, [registry]);

  const getModule = useCallback((moduleId) => {
    return registry.get(moduleId);
  }, [registry]);

  return {
    registry,
    getAccessibleModules,
    getModulesByContext,
    hasModule,
    getModule
  };
};

/**
 * Module Context Provider Component
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {UserContext} props.user - Current user information
 * @param {Permission[]} props.permissions - User permissions
 * @param {ApiClient} props.apiClient - API client for backend communication
 * @param {ThemeContext} props.theme - Theme context for styling
 * @param {ToastContext} props.toast - Toast notifications
 * @param {Object} [props.config] - Additional configuration
 * @param {boolean} [props.config.debugMode=false] - Enable debug mode for event bus
 * @param {number} [props.config.maxEventHistory=1000] - Maximum event history size
 */
export const ModuleContextProvider = ({ 
  children, 
  user, 
  permissions = [], 
  apiClient, 
  theme, 
  toast,
  config = {}
}) => {
  const [contextState, setContextState] = useState({
    initialized: false,
    error: null
  });

  // Initialize event bus configuration
  useEffect(() => {
    try {
      if (config.debugMode !== undefined) {
        moduleEventBus.setDebugMode(config.debugMode);
      }
      
      if (config.maxEventHistory !== undefined) {
        moduleEventBus.setMaxHistorySize(config.maxEventHistory);
      }

      // Always initialize - let individual modules handle their own validation
      const userId = user?.id || user?.userId || user?.sub || user?.externalId;
      // ModuleContextProvider initializing with context
      
      setContextState({ initialized: true, error: null });
    } catch (error) {
      console.error('Error initializing module context:', error);
      setContextState({ initialized: false, error: error.message });
    }
  }, [config.debugMode, config.maxEventHistory]);

  // Create scoped event bus for the current user/session
  const scopedEventBus = useMemo(() => {
    const userId = user?.id || user?.userId || user?.sub || user?.externalId;
    if (!userId) {
      return moduleEventBus;
    }
    
    return moduleEventBus.createScopedBus(
      `user-${userId}`, 
      permissions.map(p => p.id)
    );
  }, [user, permissions]);

  // Emit user context change events
  useEffect(() => {
    if (contextState.initialized && user) {
      const userId = user.id || user.userId || user.sub || user.externalId;
      moduleEventBus.emit({
        type: ModuleEventType.SYSTEM_EVENT,
        payload: {
          event: 'user_context_changed',
          user: {
            id: userId,
            role: user.role,
            permissions: permissions.map(p => p.id)
          }
        },
        source: 'module_context_provider',
        timestamp: Date.now()
      });
    }
  }, [user, permissions, contextState.initialized]);

  // Create the context value - always return an object, never null
  const contextValue = useMemo(() => {
    return {
      // Core context data
      user: user || null,
      permissions: permissions || [],
      apiClient: apiClient || null,
      theme: theme || { isDark: false }, // Provide default theme if missing
      toast: toast || { success: () => {}, error: () => {}, info: () => {} }, // Provide default toast if missing
      
      // Module system components
      registry: moduleRegistry,
      eventBus: scopedEventBus,
      
      // Context state
      initialized: contextState.initialized,
      error: contextState.error,
      
      // Utility functions
      hasPermission: (permissionId) => {
        return permissions.some(p => p.id === permissionId);
      },
      
      hasAnyPermission: (permissionIds) => {
        return permissionIds.some(id => 
          permissions.some(p => p.id === id)
        );
      },
      
      hasAllPermissions: (permissionIds) => {
        return permissionIds.every(id => 
          permissions.some(p => p.id === id)
        );
      },
      
      getUserContext: () => ({
        id: user?.id || user?.userId || user?.sub || user?.externalId,
        email: user?.email || user?.primaryEmailAddress?.emailAddress,
        name: user?.name || user?.firstName || user?.fullName,
        role: user?.role,
        organizationId: user?.organizationId,
        preferences: user?.preferences
      }),
      
      // Event utilities
      emitUserAction: (action, payload = {}) => {
        const userId = user?.id || user?.userId || user?.sub || user?.externalId;
        scopedEventBus.emit({
          type: ModuleEventType.USER_ACTION,
          payload: {
            action,
            userId,
            ...payload
          },
          source: 'user_context',
          timestamp: Date.now()
        });
      }
    };
  }, [
    user, 
    permissions, 
    apiClient, 
    theme, 
    toast, 
    scopedEventBus, 
    contextState.initialized, 
    contextState.error
  ]);

  // Show error state if there's an error
  if (contextState.error) {
    return (
      <div className="module-context-error" role="alert">
        <h3>Module System Error</h3>
        <p>Failed to initialize module context: {contextState.error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="retry-button"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <ModuleContext.Provider value={contextValue}>
      {children}
    </ModuleContext.Provider>
  );
};

/**
 * Higher-order component to inject module context
 * 
 * @param {React.ComponentType} Component - Component to wrap
 * @returns {React.ComponentType} Wrapped component with module context
 */
export const withModuleContext = (Component) => {
  const WrappedComponent = (props) => {
    const moduleContext = useModuleContext();
    return <Component {...props} moduleContext={moduleContext} />;
  };
  
  WrappedComponent.displayName = `withModuleContext(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

/**
 * Component to conditionally render based on permissions
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string|string[]} props.permissions - Required permission(s)
 * @param {boolean} [props.requireAll=false] - Whether all permissions are required
 * @param {React.ReactNode} [props.fallback] - Fallback content when permissions not met
 */
export const ModulePermissionGate = ({ 
  children, 
  permissions: requiredPermissions, 
  requireAll = false,
  fallback = null 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = useModuleContext();
  
  const permissionIds = Array.isArray(requiredPermissions) 
    ? requiredPermissions 
    : [requiredPermissions];
  
  let hasAccess = false;
  
  if (requireAll) {
    hasAccess = hasAllPermissions(permissionIds);
  } else {
    hasAccess = hasAnyPermission(permissionIds);
  }
  
  return hasAccess ? children : fallback;
};

export default ModuleContextProvider;
